import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailCampaignEditor() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_body: ''
  });

  useEffect(() => {
    if (campaignId !== 'new') {
      fetchCampaign();
    } else {
      setLoading(false);
    }
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/email-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const campaignData = response.data.find(c => c.id === campaignId);
      if (campaignData) {
        setCampaign(campaignData);
        setFormData({
          name: campaignData.name,
          subject: campaignData.subject,
          html_body: campaignData.html_body
        });
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Error al cargar campaña');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.html_body) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/email-campaigns/${campaignId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Campaña actualizada exitosamente');
      navigate('/email-campaigns');
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Error al guardar campaña');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/email-campaigns')}
          variant="ghost"
          className="mb-4 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">
            Editar Campaña
          </h1>
          
          <div className="flex gap-3">
            <Button
              onClick={() => navigate(`/email-campaigns/${campaignId}/preview`)}
              variant="outline"
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información de la Campaña</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="name">Nombre de la Campaña</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="subject">Asunto del Email</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Usa variables como {{nombre}}, {{email}}, etc."
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Puedes usar variables dinámicas en el asunto
            </p>
          </div>

          <div>
            <Label htmlFor="html_body">Contenido HTML</Label>
            <Textarea
              id="html_body"
              value={formData.html_body}
              onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
              rows={20}
              className="mt-1 font-mono text-sm"
            />
            {campaign && campaign.variables_schema && Object.keys(campaign.variables_schema).length > 0 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">📝 Variables disponibles (validadas con la base de datos):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(campaign.variables_schema).map(([category, variables]) => (
                    <div key={category} className="text-xs">
                      <span className="font-semibold text-blue-800 capitalize">{category}:</span>
                      <div className="ml-2 space-y-1">
                        {Array.isArray(variables) && variables.map((variable, idx) => (
                          <div key={idx} className="font-mono text-blue-700">{variable}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  ✅ Estas variables están conectadas con los campos reales de Clientes y Pólizas
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
